import {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react';
import type {KeyboardEvent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import type {
    ColDef,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    ValueGetterParams
} from 'ag-grid-community';
import _ from 'lodash';
import {AppGrid, GridLoading, HEADER_HEIGHT} from 'components/common/ag-grid/AppGrid.component';
import {ConfigurationPage} from 'components/common/configuration/ConfigurationPage.component';
import {
    ACTION_BUTTONS,
    ADD_BUTTON_BOTTOM_GAP,
    addButtonId,
    actionsColumn,
    cellOwnsKeysWhileEditing,
    configurationGridHeight,
    EDITING_ACTIONS_WIDTH,
    gridColumn,
    isAgGridInConfigurationEnabled,
    keepDraftLast,
    ROW_HEIGHT,
    RowActions,
    tabOutOfTheHeaderIsNotTheGrids,
    useReturnFocusAfterEdit,
    walkRowControls
} from 'components/common/ag-grid/configuration/appConfigurationGrid.function';
import {useAvailableHeight} from 'components/common/ag-grid/appGrid.function';
import {Button, ButtonType} from 'components/common/Button.component';
import Select from 'components/common/select/Select.component';
import {KEY} from 'components/common/constants';
import {Tooltip} from 'components/tooltip/Tooltip';
import {ROW_TOOLTIP_PLACE, SHARED_TOOLTIP_ID} from 'components/tooltip/AppTooltip.component';
import {useModalContext} from 'components/modals/ModalContext';
import {useTranslation} from 'components/translations/useTranslation';
import {LOADING_STATUS, MODAL_ACTIONS, MODAL_SEVERITY} from 'utils/constants';
import {handleError} from 'utils/errorHandle.function';
import * as Api from 'utils/api/api';
import type {Translate} from 'types/myTasks';
import type {
    GeneralApproverLine,
    GeneralApproversResponse,
    GeneralApproversResult,
    GeneralApproverUpdate,
    ProjectDimension,
    ProjectDraft,
    ProjectDraftStore,
    ProjectGridRow,
    ProjectOption
} from 'types/projects';
import UserSearch from '../input/UserSearch.component';
import * as reduxSelectors from '../store/application.reducers';
import Projects from './Projects.component';
import {getProjectsUsersAndRoles} from './projects.action';
import './newProjects.scss';

// the page, as its class names and ids spell it
const PAGE = 'projects';

/**
 * Row id of the draft row the Add button opens. A line that does not exist yet has no id of
 * its own, and only one draft is ever open, so a constant is all it needs.
 */
const NEW_ROW_ID = 'new-project-row';

// the columns the grid draws after the project units, in the order the classic list had them
const ROLE_COL = 'role';
const APPROVERS_COL = 'approvers';

/** One project unit column. There is one per unit the company keeps, so the id is positional. */
const dimensionCol = (index: number) => `dimension-${index}`;

/**
 * The value the Any option carries, as the classic list's own option had it: the line is left
 * open to every value of that unit.
 */
const ANY_VALUE = 0;

// the grid fills the page, keeping below it the Add button, the card's own padding and the
// page's bottom margin
const GRID_HEIGHT = configurationGridHeight(PAGE, ADD_BUTTON_BOTTOM_GAP);

// a unit's own name is short, but the values it takes are not - and there is a column per unit
const DIMENSION_MIN_WIDTH = 140;
const ROLE_MIN_WIDTH = 160;
const APPROVERS_MIN_WIDTH = 200;

// as long a list of values as a picker shows before it scrolls
const MENU_MAX_HEIGHT = 200;

// the pickers the row being edited puts in its cells, ahead of that row's own buttons
const EDITOR_INPUTS = '.new-projects__editor input:not([disabled])';
const ROW_CONTROLS = `${EDITOR_INPUTS}, ${ACTION_BUTTONS}`;

/**
 * The list a picker has open, which is drawn over the page rather than in the cell - see the
 * pickers below. It is what tells an Escape that closed a list from one meant for the row.
 */
const OPEN_PICKER_LIST = '.ga-select__menu';

// the draft row keeps to the bottom of the list, wherever the sort would otherwise put it
const keepTheDraftLast = keepDraftLast(NEW_ROW_ID);

/**
 * The grid of a company with no project units: nothing but the message saying so. ag-grid leaves
 * a 1px strip where the header row would be, which the stylesheet takes away under this class.
 */
const HEADLESS_GRID = 'new-projects__grid new-projects__grid--headless';

/** The label a project unit value shows: its own name, or Any where the line takes them all. */
function dimensionLabel(dimension: ProjectDimension | undefined, translate: Translate): string {
    return dimension?.name || translate('projects.any');
}

/**
 * The order the classic list put the lines in: by the first project unit, a line open to every
 * value of it standing under the sentinel below - which, being uppercase, sorts ahead of most
 * names. Third-clicking a heading is what returns the grid to this order.
 */
function sortLines(lines: GeneralApproverLine[]): GeneralApproverLine[] {
    return _.sortBy(lines, line => line.dimensions[0]?.name ?? 'ANY');
}

/** The values each unit can be narrowed to, in header order, with Any at the end of every list. */
function buildDimensionOptions(result: GeneralApproversResult, translate: Translate): ProjectOption[][] {
    return result.headers.map(header => {
        const values = result.possibleValues?.[header.mdmGuid] ?? [];
        const options: ProjectOption[] = values.map(value => ({value: value.mdmGuid, label: value.name}));
        options.push({value: ANY_VALUE, label: translate('projects.any')});
        return options;
    });
}

/** Every approver on the list, once each: the ids the page has to have names looked up for. */
function approverIds(lines: GeneralApproverLine[]): number[] {
    return _.uniq(_.flatMap(lines, line => line.approvers));
}

/** The options the projects slice keeps, as a name per id - see the selectors below. */
function namesById(options: ProjectOption[] | null): Map<number, string> {
    return new Map((options ?? []).map(option => [Number(option.value), option.label]));
}

/**
 * The rows the grid draws: the saved lines, and the draft under them - which is where the
 * classic list put its new row.
 */
function buildRowData(lines: GeneralApproverLine[],
                      roleNames: Map<number, string>,
                      approverNames: Map<number, string>,
                      translate: Translate,
                      showNewRow: boolean): ProjectGridRow[] {

    const rows: ProjectGridRow[] = lines.map(line => ({
        id: String(line.id),
        line,
        dimensions: line.dimensions.map(dimension => dimensionLabel(dimension, translate)),
        roleName: (line.idRole !== null && roleNames.get(line.idRole)) || '',
        // the endpoint hands back ids alone; a name still on its way leaves nothing to list
        approvers: line.approvers.map(id => approverNames.get(id)).filter(Boolean).join(', ')
    }));

    return showNewRow
        ? [...rows, {id: NEW_ROW_ID, line: null, dimensions: [], roleName: '', approvers: ''}]
        : rows;
}

/** The draft the cells of the row being edited share - see ProjectDraftStore for why. */
function createDraftStore(initial: ProjectDraft): ProjectDraftStore {
    let draft = initial;
    const listeners = new Set<() => void>();

    return {
        getDraft: () => draft,
        change: patch => {
            draft = {...draft, ...patch};
            listeners.forEach(listener => listener());
        },
        subscribe: listener => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        }
    };
}

/** What every picker in the row being edited is given. */
interface EditorProps {
    store: ProjectDraftStore;
    translate: Translate;
}

/**
 * The picker for one project unit. It keeps the value it shows itself, so a pick never
 * re-renders the page - which would rebuild the grid's columns and take this picker down with
 * them. What the row is to save goes into the shared draft instead.
 */
const DimensionEditor = ({store, index, options, autoFocus, translate}: EditorProps & {
    index: number,
    options: ProjectOption[],
    autoFocus: boolean
}) => {
    const [value, setValue] = useState<ProjectOption>(() => {
        const dimension = store.getDraft().dimensions[index];
        const picked = dimension?.mdmGuid ?? ANY_VALUE;
        // the option itself where the line's value is one the unit still offers, so the list
        // opens on it - and the line's own value where the master data no longer carries it
        return options.find(option => option.value === picked)
            ?? {value: picked, label: dimensionLabel(dimension, translate)};
    });

    const change = useCallback((option: ProjectOption | null) => {
        if (!option) {
            return;
        }
        setValue(option);
        const dimensions = store.getDraft().dimensions.slice();
        dimensions[index] = {mdmGuid: option.value, name: option.label};
        store.change({dimensions});
    }, [store, index]);

    return (
        <Select<ProjectOption>
            id={`projects-dimension-${index}`}
            options={options}
            value={value}
            onChange={change}
            isClearable={false}
            isSearchable={true}
            autoFocus={autoFocus}
            // Tab walks the row's controls (see onGridKeyDown) rather than picking what it lands on
            tabSelectsValue={false}
            maxMenuHeight={MENU_MAX_HEIGHT}
            menuPlacement="auto"
            // the grid's body scrolls and clips, so the list is drawn over the page instead -
            // against the viewport, which is what a fixed position measures from
            menuPortalTarget={document.body}
            menuPosition="fixed"
            translate={translate}
        />
    );
};

/** The picker for the role the line's approvers hold. */
const RoleEditor = ({store, options, autoFocus, translate}: EditorProps & {
    options: ProjectOption[],
    autoFocus: boolean
}) => {
    const [value, setValue] = useState<ProjectOption | null>(
        () => options.find(option => option.value === store.getDraft().idRole) ?? null);

    const change = useCallback((option: ProjectOption | null) => {
        if (!option) {
            return;
        }
        setValue(option);
        store.change({idRole: Number(option.value)});
    }, [store]);

    return (
        <Select<ProjectOption>
            id="projects-role"
            options={options}
            value={value}
            onChange={change}
            isClearable={false}
            isSearchable={true}
            autoFocus={autoFocus}
            tabSelectsValue={false}
            maxMenuHeight={MENU_MAX_HEIGHT}
            menuPlacement="auto"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            translate={translate}
        />
    );
};

/** The picker for the users holding the role, which searches the company's users as it is typed. */
const ApproversEditor = ({store, translate}: EditorProps) => {
    const [value, setValue] = useState<ProjectOption[]>(() => store.getDraft().approvers);

    const change = useCallback((selected: ProjectOption[] | null) => {
        const approvers = Array.isArray(selected) ? selected : [];
        setValue(approvers);
        store.change({approvers});
    }, [store]);

    return (
        <UserSearch propagateValue={change}
                    includeSameUser={true}
                    value={value}
                    // the first cell of the row is the one that takes focus
                    stopFocus={true}
                    multi={true}
                    tabSelectsValue={false}
                    maxMenuHeight={MENU_MAX_HEIGHT}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    translate={translate}/>
    );
};

/**
 * The Save and Cancel of the row being edited. It listens to the draft the row's pickers write
 * to, so Save turns on the moment the line is one the endpoint will take - and while it is off,
 * says what the line is still missing.
 */
const RowEditorActions = ({store, rowId, validate, save, cancel, translate}: EditorProps & {
    rowId: string,
    validate: (draft: ProjectDraft, rowId: string) => string | null,
    save: () => void,
    cancel: () => void
}) => {
    const draft = useSyncExternalStore(store.subscribe, store.getDraft);
    const problem = validate(draft, rowId);
    const tooltipId = `projects-save-tooltip-${rowId}`;

    return (
        <span className="app-grid__actions">
            {/* the hint hangs off the wrapper: a disabled button takes no pointer events of its own */}
            <span data-for={SHARED_TOOLTIP_ID}
                  data-tip={problem ? tooltipId : undefined}
                  data-place={ROW_TOOLTIP_PLACE}>
                <Button type={ButtonType.SECONDARY}
                        onClick={save}
                        disabled={!!problem}
                        text={translate('projects.save')}
                />
            </span>
            {problem && <Tooltip id={tooltipId} content={() => problem}/>}
            {/* the label the classic row wore too - projects has no cancel of its own */}
            <Button type={ButtonType.SECONDARY}
                    onClick={cancel}
                    text={translate('roles.cancel')}
            />
        </span>
    );
};

/**
 * ag-grid rendering of the Configuration -> Projects page, behind
 * fe-use-ng-grid-in-configuration. The classic list it replaces is in Projects.component.
 */
export const NewProjects = () => {

    const {translate} = useTranslation();
    const dispatch = useDispatch();
    // every popup this page opens is the modal router's
    const toggleModal = useModalContext()?.toggleModal;

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userRoles = useSelector(reduxSelectors.getUsersRoles);

    /**
     * The roles a line's approvers can hold, and the names behind the approver ids the lines
     * carry. Both are the projects slice's, filled by the page's own action - which is where the
     * classic list read them from too, a row at a time.
     */
    const projectRoles: ProjectOption[] | null = useSelector(reduxSelectors.getProjectsRoles);
    const projectUsers: ProjectOption[] | null = useSelector(reduxSelectors.getProjectsUsers);

    const [result, setResult] = useState<GeneralApproversResult | null>(null);
    const [loadingStatus, setLoadingStatus] = useState<number>(LOADING_STATUS.LOADING);

    // the draft row, and the row whose cells are open for editing - which is the draft while it
    // is being filled in. Only one row is ever in edit mode, as in the classic list
    const [showNewRow, setShowNewRow] = useState(false);
    const [editingRowId, setEditingRowId] = useState<string | null>(null);

    /** What the pickers of the row being edited have put in so far - see ProjectDraftStore. */
    const draftStore = useRef<ProjectDraftStore | null>(null);

    // the grid owns its scrolling area, and the loader stands in its place until the lines land.
    // Whichever of them is mounted is the element measured, which is why it arrives through a
    // callback
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const gridHeight = useAvailableHeight(gridElement, GRID_HEIGHT);

    const gridApi = useRef<GridApi<ProjectGridRow> | null>(null);
    const onGridReady = useCallback((event: GridReadyEvent<ProjectGridRow>) => {
        gridApi.current = event.api;
    }, []);

    // a system viewer who is not also an administrator only ever reads this page
    const readOnly = !!userRoles?.systemViewer && !userRoles?.systemAdministrator;

    /**
     * The company the request in flight was made for. A response is only taken up while that is
     * still the company in context: the slower answer to a company just left would otherwise
     * land on top of the one now on screen.
     */
    const requestedContextId = useRef(currentContextId);

    const getData = useCallback(() => {
        requestedContextId.current = currentContextId;
        setLoadingStatus(LOADING_STATUS.LOADING);
        Api.getGeneralApprovers(currentContextId).then((response: GeneralApproversResponse) => {
            if (requestedContextId.current !== currentContextId) {
                return;
            }
            setResult(response.result);
            setShowNewRow(false);
            setEditingRowId(null);
            draftStore.current = null;
            setLoadingStatus(LOADING_STATUS.DONE);
            // the roles, and a name for every approver the lines name by id
            dispatch(getProjectsUsersAndRoles(currentContextId, approverIds(response.result.lines)));
        }, (error: unknown) => {
            if (requestedContextId.current !== currentContextId) {
                return;
            }
            setLoadingStatus(LOADING_STATUS.ERROR);
            handleError(error);
        });
    }, [currentContextId, dispatch]);

    // loads on mount, and starts over whenever the company context changes - as the wrapper the
    // classic page extended did. The lines of the company being left go with it, before the new
    // ones are even asked for: they are that company's rows, but the Save and the Delete under
    // them would reach the company now in context, so none of them may stay to be pressed
    useEffect(() => {
        setResult(null);
        setShowNewRow(false);
        setEditingRowId(null);
        draftStore.current = null;
        getData();
    }, [getData]);

    const headers = useMemo (() => result?.headers ?? [], [result]);
    const lines = useMemo(() => sortLines(result?.lines ?? []), [result]);
    const dimensionOptions = useMemo(
        () => result ? buildDimensionOptions(result, translate) : [], [result, translate]);
    // memoised rather than read straight off the selectors: these feed the column definitions,
    // and a new array there would rebuild the columns - and the open pickers with them
    const roleOptions = useMemo<ProjectOption[]>(() => projectRoles ?? [], [projectRoles]);
    const roleNames = useMemo(() => namesById(projectRoles), [projectRoles]);
    const approverNames = useMemo(() => namesById(projectUsers), [projectUsers]);

    const rowData = useMemo(
        () => buildRowData(lines, roleNames, approverNames, translate, showNewRow),
        [lines, roleNames, approverNames, translate, showNewRow]);

    /** The row whose editor has just closed - see useReturnFocusAfterEdit. */
    const returnFocusTo = useReturnFocusAfterEdit(PAGE, editingRowId, rowData, gridApi);

    /** Leaves edit mode, dropping the draft row with it. */
    const cancelEdit = useCallback(() => {
        returnFocusTo.current = editingRowId;
        draftStore.current = null;
        setEditingRowId(null);
        setShowNewRow(false);
    }, [editingRowId, returnFocusTo]);

    const enterEditMode = useCallback((row: ProjectGridRow) => {
        const line = row.line;
        if (!line) {
            return;
        }
        draftStore.current = createDraftStore({
            // the line's own values, to be handed back untouched wherever the user changes nothing
            dimensions: line.dimensions.map(dimension => ({...dimension})),
            idRole: line.idRole,
            approvers: line.approvers.map(id => ({value: id, label: approverNames.get(id) ?? ''}))
        });
        setEditingRowId(row.id);
        // only one row is ever in edit mode: the Edit buttons are locked while the draft row is
        // open, and this keeps that true should a row ever be opened some other way
        setShowNewRow(false);
    }, [approverNames]);

    const addRow = useCallback(() => {
        draftStore.current = createDraftStore({
            // every unit open to any of its values, which is where the classic new row started
            dimensions: headers.map(() => ({mdmGuid: null, name: null})),
            idRole: null,
            approvers: []
        });
        setShowNewRow(true);
        setEditingRowId(NEW_ROW_ID);
    }, [headers]);

    const saveRow = useCallback(() => {
        const store = draftStore.current;
        const rowId = editingRowId;
        if (!store || rowId === null || !result) {
            return;
        }

        const draft = store.getDraft();
        const line = rowId === NEW_ROW_ID ? null : lines.find(saved => String(saved.id) === rowId);
        cancelEdit();

        const update: GeneralApproverUpdate = {
            dimensions: draft.dimensions.map(dimension => dimension.mdmGuid),
            generalApprovers: draft.approvers.map(approver => Number(approver.value)),
            headerVersion: result.version,
            id: line ? line.id : null,
            idRole: draft.idRole,
            lineVersion: line ? line.version : null
        };

        Api.updateGeneralApprovers(update, currentContextId).then(getData, (error: unknown) => {
            handleError(error);
            // whatever the endpoint refused the save over, the list it still holds is the truth
            getData();
        });
    }, [editingRowId, result, lines, cancelEdit, currentContextId, getData]);

    const deleteRow = useCallback((row: ProjectGridRow) => {
        const line = row.line;
        if (!line) {
            return;
        }
        toggleModal?.(MODAL_ACTIONS.CONFIRM_ACTION, {
            title: translate('popUp.deleteRowTitle'),
            description: translate('projects.confirmDelete'),
            confirmButtonText: 'popUp.delete',
            cancelButtonText: 'popUp.cancel',
            severity: MODAL_SEVERITY.WARNING,
            handleAction: () => {
                toggleModal(null);
                Api.deleteGeneralApprovers(line.id, currentContextId).then(getData, (error: unknown) => {
                    handleError(error);
                    getData();
                });
            }
        });
    }, [toggleModal, translate, currentContextId, getData]);

    /**
     * What the line is still missing before it can be saved, as the classic row's own check had
     * it: no two lines may carry the same project units and role, and a line needs both a role
     * and someone to hold it.
     *
     * The units are compared as they read rather than by their ids, so a line left open to any
     * value counts as the same as a saved one that already is - a pair the classic check missed,
     * the saved line carrying no value at all where the drafted one carries Any.
     */
    const validate = useCallback((draft: ProjectDraft, rowId: string): string | null => {
        const drafted = draft.dimensions.map(dimension => dimensionLabel(dimension, translate));
        const duplicate = lines.some(line => String(line.id) !== rowId
            && line.idRole === draft.idRole
            && _.isEqual(line.dimensions.map(dimension => dimensionLabel(dimension, translate)), drafted));

        if (duplicate) {
            return translate('projects.selectCombination');
        }
        if (!draft.idRole) {
            return translate('projects.selectRole');
        }
        if (draft.approvers.length === 0) {
            return translate('projects.selectRoleHolder');
        }
        return null;
    }, [lines, translate]);

    // the draft is appended, so on a list longer than the grid the Add button would otherwise
    // look like it did nothing
    useEffect(() => {
        if (showNewRow) {
            gridApi.current?.ensureIndexVisible(rowData.length - 1);
        }
    }, [showNewRow, rowData.length]);

    // a row's height is measured once, so the grid has to be told to measure them again
    useEffect(() => {
        gridApi.current?.resetRowHeights();
    }, [editingRowId, rowData]);

    /**
     * The tab order inside the grid: the headings that sort - every one but the actions - and
     * then the controls the rows carry, which are the Edit and Delete buttons of a row, and the
     * pickers with the Save and Cancel buttons of the row being edited.
     *
     * Escape gives up the row being edited, as it gave up the classic list's new row - unless a
     * picker had a list open, which is what that Escape closed. A picker answers the key either
     * way and prevents it either way, so the list still standing is what tells the two apart:
     * the first Escape closes the list, and the second gives up the row.
     */
    const onGridKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === KEY.ESCAPE) {
            if (editingRowId !== null && !document.querySelector(OPEN_PICKER_LIST)) {
                cancelEdit();
            }
            return;
        }

        // a picker that has answered the key itself keeps it
        if (!event.defaultPrevented) {
            walkRowControls(event, ROW_CONTROLS);
        }
    }, [editingRowId, cancelEdit]);

    const columnDefs = useMemo<ColDef<ProjectGridRow>[]>(() => {
        const roleLabel = translate('projects.approverRole');
        const approverLabel = translate('projects.approverEmail');
        const cellOwnsKey = cellOwnsKeysWhileEditing<ProjectGridRow>(editingRowId);

        /** Whether this is the row being edited, and so the row that draws pickers. */
        const isEditing = (row: ProjectGridRow) => row.id === editingRowId && !!draftStore.current;

        const columns: ColDef<ProjectGridRow>[] = headers.map((header, index) => gridColumn<ProjectGridRow>({
            colId: dimensionCol(index),
            headerName: header.name,
            flex: 1,
            minWidth: DIMENSION_MIN_WIDTH,
            // every line is loaded, so the grid is free to order them - and the third click on a
            // heading returns the rows to the order the endpoint handed them over in
            sortable: true,
            valueGetter: (params: ValueGetterParams<ProjectGridRow>) => params.data?.dimensions[index],
            // ag-grid cannot spot truncation inside a cell renderer, so the tooltip is asked for
            // explicitly
            tooltipValueGetter: params => params.data?.dimensions[index] || undefined,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<ProjectGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                if (!isEditing(row)) {
                    return row.dimensions[index];
                }
                return (
                    <span className="new-projects__editor" data-cy={`projects.dimension-${index}`}>
                        <DimensionEditor store={draftStore.current!}
                                         index={index}
                                         options={dimensionOptions[index] ?? []}
                                         // the row takes focus at its first cell
                                         autoFocus={index === 0}
                                         translate={translate}/>
                    </span>
                );
            }
        }));

        columns.push(gridColumn<ProjectGridRow>({
            colId: ROLE_COL,
            headerName: roleLabel,
            flex: 1,
            minWidth: ROLE_MIN_WIDTH,
            sortable: true,
            valueGetter: (params: ValueGetterParams<ProjectGridRow>) => params.data?.roleName,
            tooltipValueGetter: params => params.data?.roleName || undefined,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<ProjectGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                if (!isEditing(row)) {
                    return row.roleName;
                }
                return (
                    <span className="new-projects__editor" data-cy="projects.role">
                        <RoleEditor store={draftStore.current!}
                                    options={roleOptions}
                                    // a company with no project units opens the row here instead
                                    autoFocus={headers.length === 0}
                                    translate={translate}/>
                    </span>
                );
            }
        }), gridColumn<ProjectGridRow>({
            colId: APPROVERS_COL,
            headerName: approverLabel,
            // the approvers are a list where every other column holds a single value
            flex: 2,
            minWidth: APPROVERS_MIN_WIDTH,
            sortable: true,
            // The cell carries the row's height, either way it is read: the picker it holds while
            // the row is edited grows with the approvers on the line, and the names it reads as
            // otherwise wrap onto a line each rather than being clipped to one. A line with few
            // of them is held at the height of every other row by the stylesheet's floor.
            autoHeight: true,
            wrapText: true,
            valueGetter: (params: ValueGetterParams<ProjectGridRow>) => params.data?.approvers,
            tooltipValueGetter: params => params.data?.approvers || undefined,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<ProjectGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                if (!isEditing(row)) {
                    // the names stand off the rows above and below the way the picker does, which
                    // is what the wrapper is for - a bare string would run into them once it wraps
                    return <span className="new-projects__approvers">{row.approvers}</span>;
                }
                return (
                    <span className="new-projects__editor new-projects__editor--approvers"
                          data-cy="projects.approvers">
                        <ApproversEditor store={draftStore.current!} translate={translate}/>
                    </span>
                );
            }
        }), actionsColumn<ProjectGridRow>({
            headerName: translate('projects.actions'),
            width: EDITING_ACTIONS_WIDTH,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<ProjectGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }

                if (isEditing(row)) {
                    return (
                        <RowEditorActions store={draftStore.current!}
                                          rowId={row.id}
                                          validate={validate}
                                          save={saveRow}
                                          cancel={cancelEdit}
                                          translate={translate}/>
                    );
                }

                return (
                    <RowActions page={PAGE}
                                rowId={row.id}
                                // one row is edited at a time, as the classic list had it
                                disabled={readOnly || editingRowId !== null}
                                onEdit={() => enterEditMode(row)}
                                onDelete={() => deleteRow(row)}
                                translate={translate}/>
                );
            }
        }));

        // the heading Tab leaves the header row from - the last one it has anything to do at
        const lastSortable = _.findLast(columns, ({sortable}) => !!sortable);
        if (lastSortable) {
            lastSortable.suppressHeaderKeyboardEvent = tabOutOfTheHeaderIsNotTheGrids;
        }

        return columns;
    }, [translate, readOnly, editingRowId, headers, dimensionOptions, roleOptions, validate, saveRow,
        cancelEdit, enterEditMode, deleteRow]);

    // a reload of the same company keeps its rows on screen; a first load - and a company change,
    // which drops the rows before it asks - is waited out under the loader
    const showGrid = loadingStatus !== LOADING_STATUS.LOADING || rowData.length > 0;

    // a company with no project units gets no column per unit, and the grid narrows to the role,
    // its approvers and the actions
    const hasUnits = headers.length > 0;

    /**
     * Whether this page has anything to configure at all. A company with no project units can
     * still hold lines - a role and its approvers standing for every project there is - and those
     * are rows like any other: headings over them, and an Add under them.
     *
     * With neither, there is nothing for a heading to name and no line to add: the grid stands as
     * its empty message alone (see HEADLESS_GRID).
     */
    const hasContent = hasUnits || lines.length > 0;
    const noRowsMessage = translate(hasContent ? 'projects.noMatches' : 'generic.noData');

    return (
        <ConfigurationPage page={PAGE}
                           titleKey="projects.header"
                           infoKey="projects.headerInfo"
                           width={hasUnits ? 'w-100 mx-16' : 'w-50'}
                           translate={translate}>

            {showGrid
                ? <AppGrid<ProjectGridRow>
                    className={hasContent ? 'new-projects__grid' : HEADLESS_GRID}
                    dataCy="projects.grid"
                    containerRef={setGridElement}
                    height={gridHeight}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    getRowId={(params: GetRowIdParams<ProjectGridRow>) => params.data.id}
                    rowHeight={ROW_HEIGHT}
                    // with nothing to configure there is nothing for a heading to name, so the
                    // header row goes - and what is left of it is nothing to tab onto either
                    headerHeight={hasContent ? HEADER_HEIGHT : 0}
                    suppressHeaderFocus={!hasContent}
                    postSortRows={keepTheDraftLast}
                    onGridReady={onGridReady}
                    onKeyDown={onGridKeyDown}
                    localeText={{noRowsToShow: noRowsMessage}}
                />
                : <GridLoading containerRef={setGridElement} height={gridHeight}/>
            }

            {!readOnly && hasContent &&
                <div className="d-flex justify-content-end mt-4">
                    <Button id={addButtonId(PAGE)}
                            type={ButtonType.SECONDARY}
                            onClick={addRow}
                            disabled={editingRowId !== null}
                            text={translate("projects.add")}
                    />
                </div>
            }
        </ConfigurationPage>
    );
};

/**
 * Configuration -> Projects. The flag is read here rather than around the route, so the page
 * itself decides which of its two renderings it is.
 */
export const ProjectWrapper = () => isAgGridInConfigurationEnabled() ? <NewProjects/> : <Projects/>;

export default ProjectWrapper;
